import { supabase } from '../config/supabase';
import { createRequestId } from './requestId';
import { captureException } from './sentry';

export interface InvokeEdgeFunctionOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** When false, skip Sentry/console capture on failure (default true). */
  reportError?: boolean;
}

export interface InvokeEdgeFunctionResult<T> {
  data: T | null;
  error: Error | null;
  requestId: string;
}

/**
 * Invoke a Supabase Edge Function with X-Request-Id correlation and error reporting.
 */
export async function invokeEdgeFunction<T = unknown>(
  name: string,
  options: InvokeEdgeFunctionOptions = {}
): Promise<InvokeEdgeFunctionResult<T>> {
  const requestId = createRequestId();
  const reportError = options.reportError !== false;

  try {
    const { data, error } = await supabase.functions.invoke(name, {
      body: options.body as Record<string, unknown> | undefined,
      headers: {
        'X-Request-Id': requestId,
        ...options.headers,
      },
    });

    if (error) {
      const detail = await extractEdgeFunctionErrorMessage(error, data);
      const err = new Error(detail);

      if (reportError) {
        captureException(err, {
          source: 'edge_invoke',
          fn: name,
          requestId,
        });
      }

      return { data: null, error: err, requestId };
    }

    // Some gateways return 2xx with `{ error: "..." }` body — treat as failure.
    if (data && typeof data === 'object' && 'error' in data && (data as { error: unknown }).error) {
      const err = new Error(String((data as { error: unknown }).error));
      if (reportError) {
        captureException(err, {
          source: 'edge_invoke',
          fn: name,
          requestId,
        });
      }
      return { data: null, error: err, requestId };
    }

    return { data: data as T, error: null, requestId };
  } catch (err: unknown) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (reportError) {
      captureException(error, {
        source: 'edge_invoke',
        fn: name,
        requestId,
      });
    }
    return { data: null, error, requestId };
  }
}

async function extractEdgeFunctionErrorMessage(error: unknown, data: unknown): Promise<string> {
  if (data && typeof data === 'object' && 'error' in data && (data as { error: unknown }).error) {
    return String((data as { error: unknown }).error);
  }

  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: unknown; message?: unknown };
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      // ignore parse failures
    }
  }

  if (error instanceof Error && error.message) return error.message;
  return 'Edge function failed';
}
